<template>
  <v-layout align-start>
    <v-flex>
      <div class="sections-page">
        <!-- Header -->
        <div class="page-header">
          <div>
            <h1 class="page-title">Permisos por Rol</h1>
            <p class="page-subtitle">Configura qué secciones son visibles para cada categoría de afiliado</p>
          </div>
          <v-btn color="primary" outlined small @click="seed()">
            <v-icon left small>refresh</v-icon>
            Inicializar Secciones
          </v-btn>
        </div>

        <!-- Help text -->
        <v-alert type="info" outlined dense class="mb-4" style="font-size: 0.88rem;">
          <strong>1.</strong> Selecciona una categoria de afiliado abajo.
          <strong>2.</strong> Usa el <strong>switch</strong> para mostrar u ocultar cada seccion para esa categoria.
          <strong>3.</strong> Usa el boton <v-icon x-small>people</v-icon> para restringir una seccion a roles especificos (ej: solo Embajador).
        </v-alert>

        <!-- Tier filter buttons -->
        <div class="tier-filter">
          <span class="tier-filter-label">Categoria:</span>
          <v-btn
            v-for="t in availableTiers"
            :key="t"
            :color="selectedTier === t ? 'deep-purple' : ''"
            :dark="selectedTier === t"
            :outlined="selectedTier !== t"
            rounded
            class="mr-2 mb-2"
            @click="selectedTier = t"
          >{{ t }}</v-btn>
        </div>

        <!-- Search -->
        <v-text-field
          v-model="search"
          prepend-inner-icon="search"
          label="Buscar sección..."
          single-line
          hide-details
          outlined
          dense
          class="mb-6"
          style="max-width: 400px;"
        ></v-text-field>

        <!-- Loading -->
        <div v-if="!sections || sections.length === 0" class="text-center pa-10">
          <v-progress-circular indeterminate color="deep-purple"></v-progress-circular>
          <p class="mt-3 grey--text">Cargando secciones...</p>
        </div>

        <!-- Section groups -->
        <template v-else>
          <div v-for="group in filteredGroups" :key="group.type" class="section-group">
            <h3 class="group-title">{{ group.label }}</h3>

            <div class="section-cards">
              <div
                v-for="s in group.sections"
                :key="s._id"
                class="section-card"
                :class="{ disabled: !s.enabled }"
              >
                <div class="section-info">
                  <div class="section-label">{{ s.label }}</div>
                  <div class="section-key">{{ s.key }}</div>
                </div>

                <div class="section-actions">
                  <!-- Visible para este tier -->
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <v-switch
                        v-on="on"
                        :input-value="s.enabled && tierEnabled(s)"
                        color="deep-purple"
                        hide-details
                        dense
                        class="mt-0 mr-3"
                        @change="toggleTierForSection(s)"
                      ></v-switch>
                    </template>
                    <span>{{ tierEnabled(s) ? 'Ocultar' : 'Mostrar' }} para {{ selectedTier }}</span>
                  </v-tooltip>

                  <!-- Editar roles -->
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <v-btn v-on="on" icon x-small @click="editarRoles(s)">
                        <v-icon small>people</v-icon>
                      </v-btn>
                    </template>
                    <span>Editar roles</span>
                  </v-tooltip>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- Edit roles modal -->
        <v-dialog v-model="rolesModal" max-width="500">
          <v-card v-if="selectedItem">
            <v-card-title class="headline deep-purple white--text">
              Roles para: {{ selectedItem.label }}
            </v-card-title>
            <v-card-text class="pt-4">
              <p class="body-2 grey--text mb-3">Selecciona los roles que pueden ver esta sección.</p>
              <v-checkbox
                v-for="role in availableRoles"
                :key="role"
                v-model="selectedRoles"
                :label="role"
                :value="role"
                hide-details
                class="mt-1"
              ></v-checkbox>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn text @click="rolesModal = false">Cancelar</v-btn>
              <v-btn color="deep-purple" dark @click="guardarRoles">Guardar</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </div>
    </v-flex>
  </v-layout>
</template>

<script>
import { mapState, mapActions } from "vuex";

export default {
  data() {
    return {
      search: "",
      selectedTier: "ORO",
      rolesModal: false,
      selectedItem: null,
      selectedRoles: [],
      availableTiers: [
        "SIMPATIZANTE",
        "BRONCE",
        "PLATA",
        "ORO",
        "PREMIUM",
      ],
      availableRoles: [
        "Super Admin",
        "Administrador",
        "Editor de Blog",
        "Embajador",
        "Referente",
        "Vendedor",
        "Almacenero",
      ],
      typeLabels: {
        user: "Portal de Usuario",
      },
      typeOrder: ["user"],
    };
  },
  created() {
    this.getSections();
  },
  computed: {
    ...mapState("sectionSettingNamespace", ["sections"]),
    filteredGroups() {
      const groups = {};
      for (const s of this.sections) {
        const term = this.search.toLowerCase();
        if (term && !s.label.toLowerCase().includes(term) && !s.key.toLowerCase().includes(term)) continue;
        if (!groups[s.type]) {
          groups[s.type] = {
            type: s.type,
            label: this.typeLabels[s.type] || s.type,
            sections: [],
          };
        }
        groups[s.type].sections.push(s);
      }
      return this.typeOrder.filter(t => groups[t]).map(t => groups[t]);
    },
  },
  methods: {
    ...mapActions("sectionSettingNamespace", [
      "getSections", "toggleSection", "updateRoles", "updateTiers", "seedSections"
    ]),
    tierEnabled(section) {
      return Array.isArray(section.visibleForTiers) && section.visibleForTiers.includes(this.selectedTier);
    },
    async toggleItem(item) {
      await this.toggleSection(item._id);
    },
    async toggleTierForSection(section) {
      const current = Array.isArray(section.visibleForTiers) ? [...section.visibleForTiers] : [];
      const idx = current.indexOf(this.selectedTier);
      if (idx >= 0) {
        current.splice(idx, 1);
      } else {
        current.push(this.selectedTier);
      }
      await this.updateTiers({ id: section._id, tiers: current });
    },
    editarRoles(item) {
      this.selectedItem = item;
      this.selectedRoles = item.visibleForRoles ? [...item.visibleForRoles] : [];
      this.rolesModal = true;
    },
    async guardarRoles() {
      await this.updateRoles({ id: this.selectedItem._id, visibleForRoles: this.selectedRoles });
      this.rolesModal = false;
    },
    async seed() {
      await this.seedSections();
    },
  },
};
</script>

<style scoped>
.sections-page {
  padding: 24px;
  max-width: 960px;
  margin: 0 auto;
}
.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 24px;
}
.page-title {
  font-size: 1.7rem;
  font-weight: 700;
  margin-bottom: 4px;
}
.page-subtitle {
  color: #888;
  font-size: 0.95rem;
}
.tier-filter {
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}
.tier-filter-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #888;
  margin-right: 8px;
}
.section-group {
  margin-bottom: 32px;
}
.group-title {
  font-size: 1.1rem;
  font-weight: 700;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 2px solid rgba(255,255,255,0.1);
}
.section-cards {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.section-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  border-radius: 10px;
  border: 1px solid rgba(255,255,255,0.08);
  background: rgba(255,255,255,0.03);
  transition: box-shadow 0.2s;
}
.section-card:hover {
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
}
.section-card.disabled {
  opacity: 0.45;
}
.section-label {
  font-weight: 600;
  font-size: 0.95rem;
}
.section-key {
  font-size: 0.75rem;
  color: #999;
  margin-top: 2px;
}
.section-actions {
  display: flex;
  align-items: center;
}

/* Light theme overrides */
.theme--light .section-card {
  background: #fff;
  border-color: #eee;
}
.theme--light .group-title {
  border-bottom-color: #eee;
}
</style>
